import {
  ChevronDown,
  ExternalLink,
  FileClock,
  GitBranch,
  RefreshCw,
  Search,
  Tag,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { normalizeSearch } from '@/lib/search';
import { cn, formatNumber } from '@/lib/utils';

import { CommitItem } from '../components/commit-item';
import { UpdatesFilters } from '../components/updates-filters';
import { useUpdates } from '../hooks/use-updates';
import { formatCommitDay } from '../lib/commit-format';
import type { Commit, CommitTipo } from '../updates.types';

/** Cambios que se piden al backend en cada carga (máximo del endpoint: 500). */
const LIMITE_INICIAL = 100;
const PASO_LIMITE = 100;
const LIMITE_MAXIMO = 500;

/** Coincide el commit con el término de búsqueda. */
function coincide(commit: Commit, termino: string): boolean {
  return normalizeSearch(
    [commit.descripcion, commit.mensaje, commit.alcance, commit.autor, commit.hash_corto].join(' '),
  ).includes(termino);
}

/**
 * Actualizaciones del sistema.
 *
 * El backend lee el historial de Git del servidor (rama desplegada, autores y
 * commits convencionales), así que esta vista refleja lo que realmente se
 * publicó, sin depender de un changelog mantenido a mano.
 */
export function UpdatesPage() {
  useDocumentTitle('Actualizaciones');

  const [limite, setLimite] = useState(LIMITE_INICIAL);
  const [query, setQuery] = useState('');
  const [tipoActivo, setTipoActivo] = useState<CommitTipo | null>(null);

  const { data, isPending, isError, isFetching, refetch, isRefetching } = useUpdates(limite);

  const grupos = useMemo(() => data?.por_fecha ?? [], [data]);

  const tiposInfo = useMemo(
    () => new Map((data?.tipos ?? []).map((info) => [info.tipo, info])),
    [data],
  );

  const filtrados = useMemo(() => {
    const termino = normalizeSearch(query);
    const sinFiltro = tipoActivo === null && termino.length < 2;

    if (sinFiltro) return grupos;

    return grupos
      .map((grupo) => ({
        ...grupo,
        commits: grupo.commits.filter((commit) => {
          if (tipoActivo !== null && commit.tipo !== tipoActivo) return false;
          if (termino.length < 2) return true;

          return coincide(commit, termino);
        }),
      }))
      .filter((grupo) => grupo.commits.length > 0);
  }, [grupos, query, tipoActivo]);

  const totalFiltrado = useMemo(
    () => filtrados.reduce((acc, grupo) => acc + grupo.commits.length, 0),
    [filtrados],
  );

  const limpiar = useCallback(() => {
    setQuery('');
    setTipoActivo(null);
  }, []);

  const hayFiltros = query.trim() !== '' || tipoActivo !== null;
  const repo = data?.repo ?? null;
  const githubBase = repo?.github_url ?? null;
  const puedeAmpliar = (data?.total ?? 0) >= limite && limite < LIMITE_MAXIMO;

  return (
    <div className="space-y-6">
      {/* Encabezado */}
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">
            Bitácora del sistema
          </p>
          <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
            <FileClock className="size-6 text-brand-500" aria-hidden="true" />
            Actualizaciones
          </h1>
          <p className="mt-1 text-sm text-ink-500">
            Novedades, correcciones y mejoras publicadas en la plataforma.
          </p>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => void refetch()}
          loading={isRefetching}
          disabled={isPending}
        >
          <RefreshCw className={cn('size-4', isFetching && !isRefetching && 'animate-spin')} aria-hidden="true" />
          Actualizar
        </Button>
      </header>

      {isError ? (
        <Alert variant="error" title="No se pudieron cargar las actualizaciones">
          <div className="flex flex-wrap items-center gap-3">
            <span>Revisa la conexión con el servidor e inténtalo nuevamente.</span>
            <Button size="sm" variant="outline" onClick={() => void refetch()} loading={isRefetching}>
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : null}

      {/* El backend no pudo leer el repositorio */}
      {!isError && data && !data.disponible ? (
        <Alert variant="warning" title="Historial no disponible">
          <div className="flex flex-wrap items-center gap-3">
            <span className="max-w-2xl">
              {data.motivo ??
                'El servidor no pudo leer el historial de cambios del repositorio.'}
            </span>
            <Button size="sm" variant="outline" onClick={() => void refetch()} loading={isRefetching}>
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : null}

      {/* Información del repositorio */}
      {!isError && data?.disponible && repo ? (
        <Card className="flex flex-wrap items-center justify-between gap-4 px-5 py-4">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-ink-900 text-white">
              <GitBranch className="size-5" aria-hidden="true" />
            </span>

            <div className="min-w-0">
              <p className="truncate font-mono text-sm font-semibold text-ink-900">
                {repo.branch ?? 'rama desconocida'}
              </p>
              <p className="mt-0.5 text-xs text-ink-500">
                {formatNumber(repo.total_commits)} commits en el proyecto ·{' '}
                {formatNumber(data.total)} en pantalla
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {repo.ultima_etiqueta ? (
              <Badge variant="brand">
                <Tag className="size-3" aria-hidden="true" />
                {repo.ultima_etiqueta}
              </Badge>
            ) : null}

            {githubBase ? (
              <a
                href={githubBase}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-2 rounded-control border border-ink-200 bg-white px-3 py-1.5 text-xs font-semibold text-ink-700 transition-colors hover:border-brand-300 hover:text-brand-700"
              >
                <ExternalLink className="size-3.5" aria-hidden="true" />
                Ver repositorio
              </a>
            ) : null}
          </div>
        </Card>
      ) : null}

      {/* Filtros */}
      {!isError && data?.disponible ? (
        <UpdatesFilters
          query={query}
          onQueryChange={setQuery}
          tipos={data.tipos}
          tipoActivo={tipoActivo}
          onTipoChange={setTipoActivo}
          total={data.total}
        />
      ) : null}

      {/* Carga inicial */}
      {isPending ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-16 rounded-card" />
          ))}
        </div>
      ) : null}

      {/* Historial */}
      {!isPending && !isError && data?.disponible ? (
        filtrados.length > 0 ? (
          <section aria-label="Historial de cambios" className="space-y-6">
            <p className="text-sm text-ink-500">
              {formatNumber(totalFiltrado)}{' '}
              {totalFiltrado === 1 ? 'cambio' : 'cambios'}
              {hayFiltros ? ' con los filtros aplicados' : ' registrados'}
            </p>

            <ol className="space-y-6">
              {filtrados.map((grupo) => {
                const etiqueta = formatCommitDay(grupo.fecha);

                return (
                  <li key={grupo.fecha}>
                    <div className="flex items-center gap-3">
                      <span className="rounded-full bg-ink-900 px-2.5 py-1 text-[11px] font-semibold text-white">
                        {etiqueta.titulo}
                      </span>
                      <span className="text-xs text-ink-400">
                        {etiqueta.relativo} · {grupo.commits.length}{' '}
                        {grupo.commits.length === 1 ? 'cambio' : 'cambios'}
                      </span>
                      <span aria-hidden="true" className="h-px flex-1 bg-ink-100" />
                    </div>

                    <div className="mt-3 space-y-2 border-l border-ink-100 pl-4 sm:pl-5">
                      {grupo.commits.map((commit) => (
                        <CommitItem
                          key={commit.hash}
                          commit={commit}
                          tipoInfo={tiposInfo.get(commit.tipo)}
                          commitUrl={githubBase ? `${githubBase}/commit/${commit.hash}` : null}
                        />
                      ))}
                    </div>
                  </li>
                );
              })}
            </ol>

            {puedeAmpliar ? (
              <div className="flex flex-col items-center gap-2 border-t border-ink-100 pt-5">
                <Button
                  variant="outline"
                  onClick={() => setLimite((prev) => Math.min(prev + PASO_LIMITE, LIMITE_MAXIMO))}
                  loading={isFetching}
                >
                  <ChevronDown className="size-4" aria-hidden="true" />
                  Ver {Math.min(PASO_LIMITE, LIMITE_MAXIMO - limite)} cambios más
                </Button>
                <p className="text-xs text-ink-400">
                  Mostrando {formatNumber(data.total)} de {formatNumber(repo?.total_commits ?? 0)}{' '}
                  commits · máximo {formatNumber(LIMITE_MAXIMO)} por carga
                </p>
              </div>
            ) : null}
          </section>
        ) : (
          <div className="rounded-card border border-dashed border-ink-200 bg-white/70 px-6 py-14 text-center">
            <Search className="mx-auto size-6 text-ink-300" aria-hidden="true" />
            <h2 className="mt-3 text-base font-semibold text-ink-800">Sin resultados</h2>
            <p className="mx-auto mt-1.5 max-w-md text-sm leading-relaxed text-ink-500">
              Ningún cambio coincide con la búsqueda o el tipo seleccionado. Prueba con otro término
              o amplía el historial cargado.
            </p>

            {hayFiltros ? (
              <Button size="sm" variant="outline" className="mt-4" onClick={limpiar}>
                Limpiar filtros
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                className="mt-4"
                onClick={() => setLimite(LIMITE_MAXIMO)}
                loading={isFetching}
              >
                <ChevronDown className="size-4" aria-hidden="true" />
                Ampliar historial
              </Button>
            )}
          </div>
        )
      ) : null}
    </div>
  );
}
